import React from 'react';
import PropTypes from 'prop-types';
import { flow, get } from 'lodash-es';
import { connect } from 'react-redux';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { VisitorEntities, VisitorModel } from 'client/data/models/visitor';
import {
  CARD_VIEW,
  HEADER_COMPONENT,
  LEAD_FORM_VIEWS,
  LEAD_TYPES,
  LOADING_COMPONENT,
  THANK_YOU_VIEWS,
} from 'site-modules/shared/constants/lead-form/lead-form-params';
import { INVENTORY_TYPES } from 'client/constants/inventory-types';
import {
  FORM_TYPES,
  LEAD_TYPES as LEAD_TYPES_SUBMISSION,
  STRATEGIES,
} from 'site-modules/shared/constants/lead-form/lead-form-configs';
import {
  getPathname,
  enrichLeadsWithAdditionalData,
  getLeadSubmissionInfoWithoutEmail,
} from 'site-modules/shared/components/lead-form/utils/utils';
import { sendMarketoThankYouEmail } from 'site-modules/shared/components/lead-form/lead-form-wrapper/lead-form-wrapper-methods';
import { formatPriceStringV2 } from 'site-modules/shared/utils/price-utils';
import { LeaseDealsEmptyView } from 'site-modules/shared/components/lead-form/unified-lead-form/views/empty-view/lease-deals-empty-view/lease-deals-empty-view';
import { UnifiedLeadFormAsync } from 'site-modules/shared/components/lead-form/unified-lead-form/unified-lead-form-async';
import { isRoiTargetedFlavor } from 'client/utils/flavor-targeting';

export const getComments = ({ year, make, model, trim, payment, term, dueAtSigning, webUrls }) =>
  `This customer is interested in the ${year} ${make} ${model}${trim ? ` ${trim}` : ''} manufacturer-advertised lease special for ${formatPriceStringV2(payment)} per month. Lease program details: ${formatPriceStringV2(payment)} per month; ${term} month term; ${formatPriceStringV2(dueAtSigning)} due at signing. ${webUrls || ''}`;

export function LeaseDealsLeadFormUI({
  params,
  options,
  leadFormProps = null,
  isUserSetZip = false,
  visitorLocation = {},
}) {
  const zipCode = get(visitorLocation, 'zipCode', '');
  const {
    styleId,
    make,
    model,
    year,
    trim,
    payment,
    term,
    dueAtSigning,
    endDate,
    disclaimer,
    webUrls,
    photo,
    errorImage,
  } = params;

  const comments = getComments({ year, make, model, trim, payment, term, dueAtSigning, webUrls });
  const { creativeId } = options;

  const leadAdditionalData = { comments, interest: 'lease' };

  const customConfig = {
    leadType: LEAD_TYPES.ANY,
    creativeId,
    processDataMethods: {
      postProcessMethods: [sendMarketoThankYouEmail],
      getLeadSubmissionInfo: flow(getLeadSubmissionInfoWithoutEmail, enrichLeadsWithAdditionalData(leadAdditionalData)),
    },
    apiParams: {
      omitStyleId: true,
      omitYear: true,
    },
    loadingComponent: LOADING_COMPONENT.CONVEX,
    emptyLeadFormComponent: LeaseDealsEmptyView,
    leadFormComponent: LEAD_FORM_VIEWS.MODULAR,
    leadFormConfig: {
      headerConfig: {
        headerComponent: HEADER_COMPONENT.CONVEX,
        header: {
          text: `${formatPriceStringV2(payment)} ${make} ${model}`,
          explainingText: 'See if you qualify for this deal – check with your local dealers.',
        },
      },

      cardView: CARD_VIEW.DEALER_LIST,
      preSelectedCardNumbers: 3,
      submitButtonText: 'Get Lease Offers',
      userInfoOptions: { isLabelInside: true },
      reverseCardView: true,
      listCardView: true,
      payment,
      type: INVENTORY_TYPES.NEW,
    },
    thankYouComponent: THANK_YOU_VIEWS.LEASE_DEALS,
    thankYouConfig: {
      className: 'd-flex flex-column align-items-center',
      withFormClasses: false,
      headerConfig: {
        headerComponent: HEADER_COMPONENT.CONVEX,
      },
      params: { payment, term, dueAtSigning, endDate, disclaimer, webUrls, photo, errorImage },
      options: {
        layoutOptions: { withConvexHeader: true },
      },
    },
  };

  const formProps = {
    ...leadFormProps,
    showZipForm: !isUserSetZip,
    zipFormHeaderCopy: `${formatPriceStringV2(payment)} ${make} ${model}`,
    make,
    model,
    year,
    trim,
    styleId,
    radius: 100,
    zipCode,
    pathname: getPathname(options),
    config: {
      strategy: isRoiTargetedFlavor() ? STRATEGIES.MAKE_DL_DST_TARGET_ROI : STRATEGIES.MAKE_DL_DST,
      leadType: LEAD_TYPES_SUBMISSION.BUY,
      submitLeadsType: '',
      formType: FORM_TYPES.QUICK_QUOTES_ROI,
      dataTrackingParent: { creativeId: '' },
    },
  };

  return <UnifiedLeadFormAsync {...formProps} customConfig={customConfig} />;
}

LeaseDealsLeadFormUI.propTypes = {
  params: PropTypes.shape({
    styleId: PropTypes.number.isRequired,
    make: PropTypes.string.isRequired,
    model: PropTypes.string.isRequired,
    year: PropTypes.number.isRequired,
    trim: PropTypes.string,
    payment: PropTypes.number.isRequired,
    term: PropTypes.number.isRequired,
    dueAtSigning: PropTypes.number.isRequired,
    endDate: PropTypes.string.isRequired,
    disclaimer: PropTypes.string,
    webUrls: PropTypes.string,
    photo: PropTypes.shape({}),
    errorImage: PropTypes.string,
  }).isRequired,
  options: PropTypes.shape({
    creativeId: PropTypes.string,
  }).isRequired,
  leadFormProps: PropTypes.shape({
    closeDrawer: PropTypes.func,
  }),
  isUserSetZip: PropTypes.bool,
  visitorLocation: VisitorEntities.Location,
};

const mapStateToProps = state => ({
  isUserSetZip: !!get(state, 'visitor.location.userSet'),
});

const stateToPropsConfig = {
  visitorLocation: bindToPath('location', VisitorModel, null, false),
};

export const LeaseDealsLeadForm = connect(mapStateToProps)(connectToModel(LeaseDealsLeadFormUI, stateToPropsConfig));
